import { v4 as uuidv4 } from 'uuid';
import { authService } from './authService';

const CALENDARS_KEY = 'appointment_app_calendars';

class CalendarService {
  constructor() {
    this.initializeStorage();
    // Clean up expired data on service initialization
    this.cleanupExpiredData();
  }

  initializeStorage() {
    if (!localStorage.getItem(CALENDARS_KEY)) {
      localStorage.setItem(CALENDARS_KEY, JSON.stringify([]));
    }
  }

  generateShareId() {
    return uuidv4().split('-')[0];
  }

  async createCalendar(calendarData) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    
    // Add IDs to slots
    const slotsWithIds = (calendarData.slots || []).map(slot => ({
      ...slot,
      id: uuidv4()
    }));

    const calendar = {
      id: uuidv4(),
      ownerId: currentUser.id,
      shareId: this.generateShareId(),
      title: calendarData.title,
      description: calendarData.description,
      slots: slotsWithIds,
      settings: {
        allowMultipleBookings: calendarData.settings?.allowMultipleBookings || false,
        requireApproval: calendarData.settings?.requireApproval || false,
        bufferTime: calendarData.settings?.bufferTime || 0,
        maxBookingsPerSlot: calendarData.settings?.maxBookingsPerSlot || 1,
        timezone: calendarData.settings?.timezone || 'Europe/Berlin'
      },
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    calendars.push(calendar);
    localStorage.setItem(CALENDARS_KEY, JSON.stringify(calendars));
    return calendar;
  }

  async getUserCalendars() {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) return [];

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    return calendars.filter(cal => cal.ownerId === currentUser.id && cal.isActive);
  }

  async getCalendarByShareId(shareId) {
    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    const calendar = calendars.find(cal => cal.shareId === shareId && cal.isActive);
    
    if (!calendar) throw new Error('Kalender nicht gefunden');

    // Return calendar without sensitive owner information for public access
    const { ownerId, ...publicCalendar } = calendar;
    return publicCalendar;
  }

  async getCalendarById(id) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    const calendar = calendars.find(cal => cal.id === id && cal.ownerId === currentUser.id);
    
    if (!calendar) throw new Error('Kalender nicht gefunden');
    return calendar;
  }

  async updateCalendar(id, updateData) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    const calendarIndex = calendars.findIndex(cal => cal.id === id && cal.ownerId === currentUser.id);
    
    if (calendarIndex === -1) throw new Error('Kalender nicht gefunden');

    // Ensure slots have IDs
    if (updateData.slots) {
      updateData.slots = updateData.slots.map(slot => ({
        ...slot,
        id: slot.id || uuidv4()
      }));
    }

    calendars[calendarIndex] = {
      ...calendars[calendarIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    localStorage.setItem(CALENDARS_KEY, JSON.stringify(calendars));
    return calendars[calendarIndex];
  }

  async deleteCalendar(id) {
    const currentUser = await authService.getCurrentUser();
    if (!currentUser) throw new Error('Nicht authentifiziert');

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY));
    const calendarIndex = calendars.findIndex(cal => cal.id === id && cal.ownerId === currentUser.id);
    
    if (calendarIndex === -1) throw new Error('Kalender nicht gefunden');

    // Mark as inactive instead of deleting (for data integrity)
    calendars[calendarIndex].isActive = false;
    calendars[calendarIndex].deletedAt = new Date().toISOString();

    localStorage.setItem(CALENDARS_KEY, JSON.stringify(calendars));
    return true;
  }

  async getAvailableSlots(shareId, date) {
    const calendar = await this.getCalendarByShareId(shareId);
    const targetDate = new Date(date);
    
    return calendar.slots.filter(slot => {
      const slotDate = new Date(slot.date);
      return slotDate.toDateString() === targetDate.toDateString() && 
             slot.isAvailable && 
             (!slot.bookings || slot.bookings.length < calendar.settings.maxBookingsPerSlot);
    });
  }

  async cleanupExpiredData() {
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

    const calendars = JSON.parse(localStorage.getItem(CALENDARS_KEY) || '[]');
    const activeCalendars = calendars.filter(cal => {
      if (cal.deletedAt) {
        const deletedDate = new Date(cal.deletedAt);
        return deletedDate > threeYearsAgo;
      }
      return true;
    });

    localStorage.setItem(CALENDARS_KEY, JSON.stringify(activeCalendars));
  }
}

export const calendarService = new CalendarService();